import { useForm } from "react-hook-form";
import { roleName } from "../../lib/format";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "rizzui";
import { FieldError } from "@steelyard/theme";
import { SelectField } from "../select-field";

/**
 * `PUT /api/identity/users/{id}/role`'s one field. A `<select>` populated from
 * `lib/identity.ts`'s `listGrantableRoles` (`GET /api/identity/me/grantable-roles`), passed in by
 * the caller — this component stays a dumb form, same shape as `AssignTermsForm`
 * (`components/customers/assign-terms-form.tsx`): a plain `<form>`, `react-hook-form` + zod,
 * `onSubmit(FormData)` left to the screen.
 */
const changeRoleSchema = z.object({
  role: z.string().trim().min(1, "Required"),
});
type ChangeRoleValues = z.infer<typeof changeRoleSchema>;

export function ChangeRoleForm({
  onSubmit,
  currentRole,
  grantableRoles,
  error,
}: {
  onSubmit: (formData: FormData) => void | Promise<void>;
  currentRole: string;
  grantableRoles: string[];
  error?: unknown;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangeRoleValues>({ resolver: zodResolver(changeRoleSchema), defaultValues: { role: currentRole } });

  const submit = handleSubmit(async (values) => {
    const formData = new FormData();
    formData.set("role", values.role);
    await onSubmit(formData);
  });

  // The account being viewed may hold a role the caller's own role isn't itself allowed to grant
  // (e.g. inspecting a peer's row) — render it as an extra leading option so `defaultValues.role`
  // always matches a real <option>, rather than silently defaulting to the first grantable one.
  const holdsUngrantableRole = !grantableRoles.includes(currentRole);

  return (
    <form onSubmit={submit} noValidate className="@container flex flex-col gap-4">
      {error ? <FieldError error={error} /> : null}
      <SelectField
        label="Role"
        required
        error={errors.role?.message}
        hint="Every session this account holds is revoked the moment the role changes — they sign in again with the new role."
        {...register("role")}
      >
        {holdsUngrantableRole ? (
          <option value={currentRole}>{roleName(currentRole)} (current — not one you may grant)</option>
        ) : null}
        {grantableRoles.map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
      </SelectField>
      <div>
        <Button type="submit" disabled={isSubmitting}>
          Change role
        </Button>
      </div>
    </form>
  );
}
